import type { Dispatch, ReactElement, ReactNode, SetStateAction } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Card, Collapse } from 'reactstrap';
import { v4 as uuid } from 'uuid';

/**
 * A hook to generate accessible props for an accordion, and its associated state
 *
 * @returns ButtonProps:  should be applied to a button, this is to help with semantic html
 * @returns ContentProps: could be applied to a div
 */
export const useAccordion = ({ id = undefined, initialOpen = false }: Props = {}): [
  ButtonProps,
  ContentProps,
  {
    accordionId: ID;
    isOpen: boolean;
    toggleOpen(): void;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
  }
] => {
  const [accordionId] = useState<ID>(id || uuid());
  const [isOpen, setIsOpen] = useState(initialOpen);
  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const [button, content] = useMemo(() => {
    const buttonId = `${accordionId}-button`;
    const contentId = `${accordionId}-content`;

    return [
      {
        id: buttonId,
        onClick: toggleOpen,
        'aria-expanded': isOpen,
        'aria-controls': contentId
      },
      { id: contentId, 'aria-hidden': !isOpen, role: 'region', 'aria-labelledby': buttonId }
    ];
  }, [accordionId, isOpen, toggleOpen]);

  return [button, content, { accordionId, isOpen, toggleOpen, setIsOpen }];
};

export type AccordionProps = {
  header: ReactNode;
  className?: string;
  cardHeaderClassName?: string;
};

/**
 * An accessible accordion
 * based on: https://www.w3.org/TR/wai-aria-practices-1.1/examples/accordion/accordion.html
 */
export function Accordion({
  header,
  cardHeaderClassName,
  className,
  children,
  ...props
}: React.PropsWithChildren<AccordionProps>): ReactElement {
  const [buttonProps, contentProps] = useAccordion({ initialOpen: true });

  return (
    <Card
      data-testid={props['data-testid']}
      className={clsx('mx-0 my-2', className, {
        'd-print-none': contentProps['aria-hidden']
      })}
    >
      <button
        type="button"
        id={buttonProps.id}
        onClick={buttonProps.onClick}
        aria-expanded={buttonProps['aria-expanded']}
        aria-controls={buttonProps['aria-controls']}
        className={clsx(
          cardHeaderClassName,
          'btn card-header d-flex justify-content-between align-items-center cursor-pointer w-100'
        )}
      >
        {header}
        <FontAwesomeIcon
          icon={buttonProps['aria-expanded'] ? 'compress-alt' : 'expand-alt'}
          className="ms-2 d-print-none"
        />
      </button>
      <Collapse {...contentProps} isOpen={!contentProps['aria-hidden']}>
        {children}
      </Collapse>
    </Card>
  );
}

type ID = number | string;

type Props = {
  id?: ID;
  initialOpen?: boolean;
};

type ButtonProps = {
  id: string;
  onClick(): void;
  'aria-expanded': boolean;
  'aria-controls': string;
};

type ContentProps = { id: string; 'aria-hidden': boolean; 'aria-labelledby': string };
